import { useAnimation, useMotionValue } from "motion/react";
import type { ComponentProps } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuRotateCw } from "react-icons/lu";

import { Spinner } from "@/components/shared/spinner";

type BasePullToRefreshIndicatorProps = {
  y: ReturnType<typeof useMotionValue<number>>;
  progress: ReturnType<typeof useMotionValue<number>>;
  releaseText?: string;
  triggered: boolean;
  triggerThreshold: number;
};

const BasePullToRefreshIndicator = ({
  y,
  progress,
  releaseText = "Release to refresh...",
  triggered,
  triggerThreshold,
}: BasePullToRefreshIndicatorProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null!);
  const iconRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribeY = y.on("change", (value: number) => {
      if (!wrapperRef.current) return;

      wrapperRef.current.style.height = `${value}px`;
    });
    const unsubscribeProgress = progress.on("change", (value: number) => {
      if (!iconRef.current || !textRef.current) return;

      const opacity = Math.min(value / triggerThreshold, 1);
      iconRef.current.style.opacity = `${opacity}`;
      iconRef.current.style.transform = `rotate(${value * 3}deg)`;
      textRef.current.style.opacity = Number(
        value >= triggerThreshold,
      ).toString();
    });

    // Set initial
    wrapperRef.current.style.height = `${y.get()}px`;

    return () => {
      unsubscribeY();
      unsubscribeProgress();
    };
  }, [y, progress, triggerThreshold]);

  return (
    <div
      ref={wrapperRef}
      className="
        absolute inset-x-0 top-0 flex h-0 flex-col items-center justify-center
        overflow-hidden
      "
    >
      {!triggered && (
        <>
          <div ref={iconRef} className="text-primary opacity-0">
            <LuRotateCw size={30} />
          </div>
          <div
            className="text-xs text-muted opacity-0 transition-opacity"
            ref={textRef}
          >
            {releaseText}
          </div>
        </>
      )}
      {triggered && <Spinner size={28} delay={0} />}
    </div>
  );
};

export const usePullToRefresh = ({
  /** The maximum length the user can pull */
  maximumPullLength = 150,
  /** Use this value while the pull to refresh is in triggered state */
  triggeredHeight = 80,
  /** The percentage it needs to reach to trigger the refresh */
  triggerThreshold = 40,
  /** If the pull to refresh should be disabled completely */
  disabled = false,
  scrollElement = typeof window !== "undefined" ? window : undefined,
  elasticity = 0.3,
} = {}) => {
  const y = useMotionValue(0);
  const controls = useAnimation();
  const [triggered, setTriggered] = useState(false);
  const progress = useMotionValue(0);
  const touchStartPositionYRef = useRef(0);
  const touchStartPositionXRef = useRef(0);
  const horizontallyScrollingRef = useRef(false);
  const motionDivRef = useRef<HTMLDivElement>(null!);
  const enabledOnTouchStartRef = useRef(true);

  useEffect(() => {
    if (!triggered) {
      controls.start({ y: 0 });
    } else {
      controls.start({ y: triggeredHeight });
    }
  }, [controls, triggered, triggeredHeight]);

  useEffect(() => {
    const motionDiv = motionDivRef.current;

    const enabled = () =>
      !disabled &&
      scrollElement?.scrollY === 0 &&
      !horizontallyScrollingRef.current;

    const onTouchStart = (ev: TouchEvent) => {
      enabledOnTouchStartRef.current = enabled();
      if (!enabled()) return;

      touchStartPositionYRef.current = ev.touches[0].clientY;
      touchStartPositionXRef.current = ev.touches[0].clientX;
    };

    const onTouchMove = (ev: TouchEvent) => {
      if (!enabled() || !enabledOnTouchStartRef.current) return;

      ev.stopPropagation();

      const touchYDistance =
        ev.touches[0].clientY - touchStartPositionYRef.current;
      const touchXDistance =
        ev.touches[0].clientX - touchStartPositionXRef.current;

      // Prevent body scroll if pull is in progress
      if (y.get() > 0) {
        ev.preventDefault();
      }

      if (Math.abs(touchXDistance) > touchYDistance) {
        horizontallyScrollingRef.current = true;
        return;
      }

      if (y.get() >= maximumPullLength) {
        y.set(maximumPullLength);
      } else if (touchYDistance < 0) {
        y.set(0);
      } else {
        y.set(touchYDistance * elasticity);
      }
      progress.set(y.get() / (maximumPullLength / 100));
    };

    const onTouchEnd = () => {
      horizontallyScrollingRef.current = false;
      enabledOnTouchStartRef.current = true;

      if (enabled() && progress.get() >= triggerThreshold) {
        setTriggered(true);
      } else {
        controls.start({ y: 0 });
      }
    };

    motionDiv.addEventListener("touchstart", onTouchStart);
    motionDiv.addEventListener("touchmove", onTouchMove);
    motionDiv.addEventListener("touchend", onTouchEnd);

    return () => {
      motionDiv.removeEventListener("touchstart", onTouchStart);
      motionDiv.removeEventListener("touchmove", onTouchMove);
      motionDiv.removeEventListener("touchend", onTouchEnd);
    };
  }, [
    controls,
    elasticity,
    disabled,
    maximumPullLength,
    progress,
    triggerThreshold,
    y,
    scrollElement,
  ]);

  const PullToRefreshIndicator = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      (
        props: Omit<
          ComponentProps<typeof BasePullToRefreshIndicator>,
          "y" | "progress" | "triggerThreshold" | "triggered"
        >,
      ) => (
        <BasePullToRefreshIndicator
          y={y}
          progress={progress}
          triggerThreshold={triggerThreshold}
          triggered={triggered}
          {...props}
        />
      ),
    [y, progress, triggerThreshold, triggered],
  );

  return {
    PullToRefreshIndicator,
    triggered,
    setTriggered,
    y,
    controls,
    progress,
    motionProps: {
      animate: controls,
      style: { y },
      ref: motionDivRef,
    },
  };
};
